import { numeric, pgTable, text, timestamp } from 'drizzle-orm/pg-core';

/**
 * Tabela `cotacoes` — CACHE da cotação de moeda estrangeira (hoje só USD→BRL).
 *
 * MESMA NATUREZA de `railway_costs` (ver o cabeçalho de `railway-costs.ts`): não
 * é dado de negócio, é espelho descartável de uma API pública, guardado para não
 * repetir a consulta a cada request. Se a tabela for truncada, a próxima
 * abertura da tela a reconstrói sozinha.
 *
 * POR QUE UMA TABELA SEPARADA, E NÃO UMA COLUNA EM `railway_costs`:
 * as duas coisas envelhecem em ritmos MUITO diferentes. O custo do Railway é
 * reconsultado a cada `MASTERAPPS_RAILWAY_TTL_SECONDS` (padrão 60 s); o PTAX é
 * publicado UMA vez por dia útil. Guardar a cotação junto do snapshot de custo
 * faria o Banco Central ser consultado a cada minuto para receber sempre o mesmo
 * número — desrespeitoso com uma API pública e gratuita, e mais um ponto de
 * falha em cada refresh de custo. Com a tabela própria, cada um tem o seu TTL.
 *
 * MODELAGEM: uma linha por PAR de moedas (`par` é a PK natural, ex.: 'USD-BRL'),
 * o que dá o mesmo `ON CONFLICT DO UPDATE` idempotente do cache de custos.
 *
 * `data_cotacao` é a data da PUBLICAÇÃO no Banco Central (não o momento em que
 * buscamos, que é o `fetched_at`). Os dois campos respondem perguntas
 * diferentes: `fetched_at` diz há quanto tempo falamos com o BCB, `data_cotacao`
 * diz de que dia é o número — e é `data_cotacao` que define se a cotação ainda
 * pode ser usada (ver `MAX_IDADE_DIAS` em `services/cotacao-dolar.ts`).
 */
export const cotacoes = pgTable('cotacoes', {
  /** Par de moedas no formato `ORIGEM-DESTINO`. Hoje só existe 'USD-BRL'. */
  par: text('par').primaryKey(),
  /**
   * Quantas unidades da moeda de destino valem UMA da moeda de origem
   * (USD-BRL = quantos reais vale 1 dólar).
   *
   * `mode: 'number'` pelo mesmo motivo de `applications.projetoHorasDia`: sem
   * ele o driver devolve a coluna `numeric` como STRING e a multiplicação
   * viraria concatenação. 6 casas decimais porque o PTAX publica 5 (5,15270) e
   * uma casa de folga evita arredondar a fonte.
   */
  valor: numeric('valor', { precision: 12, scale: 6, mode: 'number' }).notNull(),
  /** Data da publicação usada, em 'YYYY-MM-DD' (o dia útil do boletim PTAX). */
  dataCotacao: text('data_cotacao').notNull(),
  /**
   * Qual ponta do PTAX gerou o valor ('venda' hoje). Fica gravado para a linha
   * ser AUTODESCRITIVA: se um dia o código trocar de ponta, o registro antigo
   * continua dizendo de onde veio, em vez de ser reinterpretado pela constante
   * nova do código.
   */
  tipo: text('tipo').notNull(),
  /** Quando falamos com a fonte — é o que a janela de TTL compara. */
  fetchedAt: timestamp('fetched_at', { withTimezone: true }).notNull().defaultNow(),
});

export type CotacaoRow = typeof cotacoes.$inferSelect;
export type NewCotacaoRow = typeof cotacoes.$inferInsert;
